import type { FC } from "react";
import { useTheme } from "@/context/ThemeContext";
import { useThemePreview } from "@/hooks/useThemePreview";
import type {
  BaseComponentProps,
  ComponentSize,
} from "@/types/component-props";
import { cn } from "@/utils/cn";
import {
  themeColors,
  themeComponentStyles,
  themeData,
} from "@/utils/themeUtils";

export interface ThemeToggleGroupProps extends BaseComponentProps {
  /** Delay in ms before showing theme preview when hovering (default: 300ms) */
  previewDelay?: number;
  /** Whether to disable the preview functionality (default: false) */
  disablePreview?: boolean;
  /** Size of the selector */
  size?: ComponentSize;
}

/**
 * Theme palette selector with radio-style UI
 * A compact, radio-button style component for selecting color palettes
 * Focuses ONLY on theme color selection (not dark/light mode)
 */
export const ThemeToggleGroup: FC<ThemeToggleGroupProps> = ({
  className,
  previewDelay = 300,
  disablePreview = false,
  size = "default",
}) => {
  const { theme, setTheme, previewTheme } = useTheme();

  // Use theme preview hook to manage preview behavior
  const { handleMouseEnter, handleMouseLeave } = useThemePreview(previewTheme, {
    delay: previewDelay,
    disabled: disablePreview,
  });

  // Available themes (color palettes)
  const themes = themeData.getThemeOptions();

  return (
    <div
      className={cn(
        "inline-flex justify-center items-center",
        themeComponentStyles.getContainerClasses({ withExtraPadding: true }),
        className,
      )}
    >
      <div className="flex gap-2">
        {/* Theme buttons (color palette selection) */}
        {themes.map((themeOption) => {
          const isActive = theme === themeOption.id;

          return (
            <button
              aria-label={`${themeOption.label} theme`}
              aria-pressed={isActive}
              className={cn(
                size === "sm"
                  ? "w-4 h-4"
                  : size === "lg"
                    ? "w-6 h-6"
                    : "w-5 h-5",
                "rounded-full cursor-pointer transition-transform-standard hover:scale-110 shadow-sm",
                themeColors.getColorIndicatorClass({
                  themeId: themeOption.id,
                }),
                isActive && "ring-2 ring-offset-2",
                isActive &&
                  themeColors.getRingColorClass({
                    themeId: themeOption.id,
                  }),
                themeComponentStyles.getControlClasses(),
              )}
              data-theme={themeOption.id}
              key={themeOption.id}
              onClick={() => setTheme(themeOption.id)}
              onMouseEnter={() => handleMouseEnter(themeOption.id)}
              onMouseLeave={handleMouseLeave}
              title={themeOption.label}
              type="button"
            />
          );
        })}
      </div>
    </div>
  );
};
